/*
 * Finishes the product grids AICO renders into a news body (`[data-aico-product-grid]`, drawn by
 * the AICO Neo builder). That body is the same for every shop, so this adds what differs per shop:
 * the product link of this shop, the price and the cart button (rendered by
 * templates/product.aico-news-card.liquid with this shop's price rules) and the filter bar.
 * The cards are drawn the way the builder canvas draws them (aiconeo ProductGridRenderer): framed
 * card, picture area, brand, name, description, price and button, with the block's settings read
 * from the article's builder document (`contentBuilderNeoJson`) that the news scripts load.
 * Loaded by templates/page.news.liquid, which sets `window.aico_shop_domain`.
 */
(function () {
  var shopDomain = window.aico_shop_domain || '';
  var language = (document.documentElement.lang || 'de').slice(0, 2).toLowerCase();
  var allLabels = { de: 'Alle', fr: 'Tous', it: 'Tutti', en: 'All' };
  var stockLabels = { de: 'An Lager', fr: 'En stock', it: 'Disponibile', en: 'In stock' };
  // The builder's fallbacks when neither the page nor the block sets a colour (aiconeo
  // ElementRenderer accentColor, renderers/filterPillTheme.ts).
  var accentFallback = '#5A4949';
  var pillBackgroundFallback = '#2C2A2A';
  var pillBorderFallback = '#6E5F5D';
  // The canvas's picture for a product without one: lucide `package` (ISC licence), as the builder draws it.
  var packageIcon = '<svg class="aico-news-card-placeholder" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"'
    + ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + '<path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/>'
    + '<path d="M12 22V12"/><polyline points="3.29 7 12 12 20.71 7"/><path d="m7.5 4.27 9 5.15"/></svg>';
  // The canvas button icons: lucide `shopping-cart` for the cart button, `arrow-right` for the details link.
  var iconStart = '<svg class="aico-news-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"'
    + ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">';
  var cartIcon = iconStart + '<circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/>'
    + '<path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>';
  var arrowIcon = iconStart + '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>';
  var neoDocument = null;

  function injectStyles() {
    if (document.getElementById('aico-product-grid-styles')) {
      return;
    }
    var style = document.createElement('style');
    style.id = 'aico-product-grid-styles';
    // Values of the builder canvas (aiconeo ProductGridRenderer, measured with the Mediconsult shop
    // theme selected): the name and paragraphs keep the shop's own heading and text styles, as on
    // the canvas. !important only beats the inline styles AICO puts on the card for themes without
    // this script. The columns stay AICO's (the builder's column count).
    // news.css keeps line breaks in the article body (white-space:pre-line); the card markup must not.
    style.textContent = '[data-aico-product-grid]{gap:16px!important;align-items:stretch}'
      + '[data-aico-product-grid] .content-type-neo-product-card{display:flex;flex-direction:column;overflow:hidden;text-align:left;white-space:normal;'
      + 'background:var(--aico-card-background,#fff);color:var(--aico-card-color,inherit);border:1px solid #e1e7ef;border-radius:12px;'
      + 'box-shadow:0 1px 2px rgba(0,0,0,.05);transition:box-shadow .2s}'
      + '[data-aico-product-grid] .content-type-neo-product-card:hover{box-shadow:0 10px 15px -3px rgba(0,0,0,.1),0 4px 6px -4px rgba(0,0,0,.1)}'
      + '[data-aico-product-grid] .content-type-neo-product-card > a{display:flex!important;flex-direction:column;flex:1 1 auto}'
      + '.aico-news-card-media{position:relative;display:flex;align-items:center;justify-content:center;overflow:hidden;aspect-ratio:1/1;background:#f1f5f9}'
      + '[data-aico-product-grid][data-layout="grid-large"] .aico-news-card-media{aspect-ratio:4/3}'
      + '.aico-news-card-media img{width:100%;height:100%;padding:12px;box-sizing:border-box;object-fit:contain;transition:transform .3s}'
      + '[data-aico-product-grid] .content-type-neo-product-card:hover .aico-news-card-media img{transform:scale(1.05)}'
      + '.aico-news-card-placeholder{width:48px;height:48px;color:rgba(101,117,139,.3)}'
      + '.aico-news-card-stock{position:absolute;top:50%;right:8px;transform:translateY(-50%);padding:4px 8px;border:1px solid currentColor;border-radius:9999px;font-size:11px;line-height:16.5px;opacity:.7}'
      + '.aico-news-card-body{display:flex;flex-direction:column;flex:1 1 auto;padding:16px}'
      + '.aico-news-card-body h3{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;min-height:2.5em;word-break:break-word}'
      + '.aico-news-card-brand{font-size:12px;line-height:18px;color:#65758b}'
      + '.aico-news-card-description{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;font-size:12px;line-height:18px;color:#65758b}'
      // Plain cards (block card style «plain»): no frame, no grey picture area.
      + '[data-aico-product-grid][data-card-style="plain"] .content-type-neo-product-card{background:transparent;border:0;box-shadow:none}'
      + '[data-aico-product-grid][data-card-style="plain"] .aico-news-card-media{background:transparent}'
      + '.aico-news-card-extras{margin-top:auto;padding:0 16px 16px}'
      + '.aico-news-card-price{margin-bottom:12px;font-size:18px;font-weight:700;line-height:27px;color:#0d80f2}'
      + '.aico-news-card-price .was_price{margin-left:8px;font-size:14px;font-weight:400;color:#8c8b8b;text-decoration:line-through}'
      + '.aico-news-card-price .tax_note{margin-left:4px;font-size:12px;font-weight:400;color:#65758b}'
      // The shop hides every product form outside the product page (.shopify-product-form{display:none!important}).
      + '.aico-news-card-extras .aico-news-card-form{display:block!important;margin:0}'
      + '.aico-news-card-icon{width:16px;height:16px;flex-shrink:0}'
      + '.aico-news-card-extras .action_button{display:inline-flex;align-items:center;justify-content:center;gap:8px;width:auto;max-width:none;height:44px;margin:0;padding:0 20px;'
      + 'border:0;border-radius:100px;background:var(--aico-button-background,' + accentFallback + ');color:var(--aico-button-color,#fff);font-size:14px;line-height:16.8px;text-transform:uppercase;text-decoration:none}'
      + '.aico-product-grid-filters{display:flex;margin:0 0 24px}'
      + '.aico-product-grid-pill{display:inline-flex;align-items:center;gap:4px;max-width:100%;overflow-x:auto;padding:6px;border-radius:9999px;'
      + 'border:1px solid var(--aico-pill-border,' + pillBorderFallback + ');background:var(--aico-pill-background,' + pillBackgroundFallback + ')}'
      // Inactive options keep the shop's button colour, as on the canvas, which applies the shop theme's styles.
      + '.aico-product-grid-filter{display:flex;align-items:center;flex-shrink:0;height:44px;margin:0;padding:0 20px;border:0;border-radius:100px;'
      + 'font-size:14px;line-height:16.8px;letter-spacing:normal;text-transform:uppercase;white-space:nowrap;color:rgba(255,255,255,.7)}'
      + '.aico-product-grid-filter:hover{color:#fff}'
      + '.aico-product-grid-filter.is-active{background:var(--aico-filter-active,' + accentFallback + ');color:#fff}';
    document.head.appendChild(style);
  }

  function parseJson(value, fallback) {
    try {
      return JSON.parse(value) || fallback;
    } catch (error) {
      return fallback;
    }
  }

  function findCardLink(card) {
    return card.querySelector(':scope > a');
  }

  // The product is not sold in this shop: keep the card, drop the link that would end in a 404.
  function removeCardLink(card) {
    var link = findCardLink(card);
    if (!link) {
      return;
    }
    while (link.firstChild) {
      card.insertBefore(link.firstChild, link);
    }
    card.removeChild(link);
  }

  // Point the card at this shop's product page; a link the author set elsewhere is kept.
  function linkCardTo(card, handle, handlesByShop) {
    var url = '/products/' + encodeURIComponent(handle);
    var link = findCardLink(card);
    if (link) {
      var isProductLink = Object.keys(handlesByShop).some(function (domain) {
        return link.getAttribute('href') === '/products/' + encodeURIComponent(handlesByShop[domain]);
      });
      if (isProductLink) {
        link.setAttribute('href', url);
      }
      return;
    }
    link = document.createElement('a');
    link.href = url;
    link.style.cssText = 'display:block;color:inherit;text-decoration:none';
    while (card.firstChild) {
      link.appendChild(card.firstChild);
    }
    card.appendChild(link);
  }

  // The canvas card: a picture area (empty when the product has no picture, so a row stays level)
  // over a body with the name and the description. AICO's inline sizes are dropped so the shop's
  // heading and text styles apply, as on the canvas.
  function buildCardLayout(card) {
    var holder = findCardLink(card) || card;
    var media = document.createElement('div');
    media.className = 'aico-news-card-media';
    var image = holder.querySelector('img');
    if (image) {
      image.removeAttribute('style');
      media.appendChild(image);
    } else {
      media.innerHTML = packageIcon;
    }
    var body = document.createElement('div');
    body.className = 'aico-news-card-body';
    while (holder.firstChild) {
      body.appendChild(holder.firstChild);
    }
    body.querySelectorAll('h3, p').forEach(function (element) {
      element.removeAttribute('style');
      if (element.tagName === 'P') {
        element.classList.add('aico-news-card-description');
      }
    });
    holder.appendChild(media);
    holder.appendChild(body);
  }

  function addShopDetails(grid, card, handle) {
    var isPriceShown = grid.getAttribute('data-show-price') === '1';
    var isButtonShown = grid.getAttribute('data-show-button') === '1';

    fetch('/products/' + encodeURIComponent(handle) + '?view=aico-news-card', { credentials: 'same-origin' })
      .then(function (response) {
        // Only a 404 means the shop does not sell the product; any other failure keeps the link.
        if (response.status === 404) {
          removeCardLink(card);
        }
        return response.ok ? response.text() : '';
      })
      .then(function (markup) {
        var holder = document.createElement('div');
        holder.innerHTML = markup;
        var extras = holder.querySelector('.aico-news-card-extras');
        if (!extras) {
          return;
        }
        var media = card.querySelector('.aico-news-card-media');
        if (media && extras.getAttribute('data-available') === 'true') {
          var stock = document.createElement('span');
          stock.className = 'aico-news-card-stock';
          stock.textContent = stockLabels[language] || stockLabels.de;
          media.appendChild(stock);
        }
        if (!isPriceShown) {
          extras.querySelectorAll('.aico-news-card-price').forEach(function (price) { price.remove(); });
        }
        if (!isButtonShown) {
          extras.querySelectorAll('.aico-news-card-form, .aico-news-card-details, .aico-news-card-request').forEach(function (button) { button.remove(); });
        }
        // The author's label is German and names the cart; other languages and the details link keep the shop's own.
        var button = extras.querySelector('.aico-news-card-form .aico-news-card-button');
        var buttonText = grid.getAttribute('data-button-text');
        if (button && buttonText && language === 'de') {
          button.textContent = buttonText;
        }
        if (button) {
          button.insertAdjacentHTML('afterbegin', cartIcon);
        }
        // The details link and, for a price on request, the link to the product page's request form.
        extras.querySelectorAll('.aico-news-card-details, .aico-news-card-request').forEach(function (link) {
          link.insertAdjacentHTML('afterbegin', arrowIcon);
        });
        if (extras.children.length > 0) {
          card.appendChild(extras);
        }
      })
      .catch(function () {});
  }

  function buildFilterBar(grid) {
    if (grid.getAttribute('data-show-filters') !== '1') {
      return;
    }
    var enabledKeys = parseJson(grid.getAttribute('data-filter-keys'), []);
    var cards = Array.prototype.slice.call(grid.children);
    var categories = [];
    var isKnown = {};
    cards.forEach(function (card) {
      var key = card.getAttribute('data-category-key');
      var name = card.getAttribute('data-category-name');
      if (!key || !name || isKnown[key] || (enabledKeys.length > 0 && enabledKeys.indexOf(key) === -1)) {
        return;
      }
      isKnown[key] = true;
      categories.push({ key: key, name: name });
    });
    if (categories.length === 0) {
      return;
    }

    var bar = document.createElement('div');
    bar.className = 'aico-product-grid-filters';
    var pill = document.createElement('div');
    pill.className = 'aico-product-grid-pill';
    bar.appendChild(pill);
    var buttons = [{ key: '__all__', name: allLabels[language] || allLabels.de }].concat(categories).map(function (category) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'aico-product-grid-filter';
      button.textContent = category.name;
      button.setAttribute('data-filter-key', category.key);
      button.addEventListener('click', function () { applyFilter(category.key); });
      pill.appendChild(button);
      return button;
    });

    function applyFilter(activeKey) {
      buttons.forEach(function (button) {
        button.classList.toggle('is-active', button.getAttribute('data-filter-key') === activeKey);
      });
      cards.forEach(function (card) {
        card.style.display = activeKey === '__all__' || card.getAttribute('data-category-key') === activeKey ? '' : 'none';
      });
    }

    grid.parentNode.insertBefore(bar, grid);
    var defaultKey = grid.getAttribute('data-default-filter');
    applyFilter(defaultKey && isKnown[defaultKey] ? defaultKey : '__all__');
  }

  // The builder document of the article, as the news scripts load it (one article, with its
  // `contentBuilderNeoJson`). List and category requests carry no single document and are skipped.
  function rememberNeoDocument(payload) {
    var items = payload && payload.data;
    var item = Array.isArray(items) ? (items.length === 1 ? items[0] : null) : items;
    var builderDocument = item && item.attributes ? item.attributes.contentBuilderNeoJson : null;
    if (typeof builderDocument === 'string') {
      builderDocument = parseJson(builderDocument, null);
    }
    if (builderDocument && Array.isArray(builderDocument.sections)) {
      neoDocument = builderDocument;
      applyBuilderSettings();
    }
  }

  function collectProductGridElements(node, found) {
    if (Array.isArray(node)) {
      node.forEach(function (child) { collectProductGridElements(child, found); });
    } else if (node && typeof node === 'object') {
      if (node.type === 'product-grid') {
        found.push(node);
      }
      Object.keys(node).forEach(function (key) { collectProductGridElements(node[key], found); });
    }
    return found;
  }

  function handlesKey(handlesByShop) {
    return Object.keys(handlesByShop).sort().map(function (domain) { return domain + '=' + handlesByShop[domain]; }).join('|');
  }

  // The product of a card: by its handles per shop, else by its name in any language.
  function findCardProduct(card, products) {
    var cardHandles = handlesKey(parseJson(card.getAttribute('data-product-handles'), {}));
    var heading = card.querySelector('h3');
    var name = heading ? heading.textContent.trim() : '';
    return products.filter(function (product) {
      if (cardHandles !== '') {
        var productHandles = {};
        (product.productHandles || []).forEach(function (entry) {
          if (entry && entry.shopifyDomain && entry.productHandle) {
            productHandles[String(entry.shopifyDomain).trim()] = String(entry.productHandle).trim();
          }
        });
        return handlesKey(productHandles) === cardHandles;
      }
      var translations = product.translations || {};
      return Object.keys(translations).some(function (locale) {
        var translation = translations[locale] || {};
        return [translation.name, translation.webName].some(function (value) {
          return typeof value === 'string' && value.trim() === name;
        });
      });
    })[0] || null;
  }

  // The text colour the canvas pairs with a card colour (aiconeo productResolvers resolveCardSurface).
  function readableTextOn(color) {
    var hex = String(color).trim().replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(function (digit) { return digit + digit; }).join('');
    }
    if (!/^[0-9a-f]{6}$/i.test(hex)) {
      return '#ffffff';
    }
    var channel = function (value) {
      var share = value / 255;
      return share <= 0.04045 ? share / 12.92 : Math.pow((share + 0.055) / 1.055, 2.4);
    };
    var luminance = 0.2126 * channel(parseInt(hex.slice(0, 2), 16)) + 0.7152 * channel(parseInt(hex.slice(2, 4), 16)) + 0.0722 * channel(parseInt(hex.slice(4, 6), 16));
    return luminance > 0.45 ? '#111111' : '#ffffff';
  }

  // The block's settings from the builder document, as the canvas applies them. Grids are matched
  // to the document's product grids in order; when the counts differ the canvas defaults stay.
  function applyBuilderSettings() {
    var grids = Array.prototype.slice.call(document.querySelectorAll('[data-aico-product-grid][data-aico-finished]'));
    if (!neoDocument || grids.length === 0) {
      return;
    }
    var settings = neoDocument.settings || {};
    var accent = settings.accentColor || accentFallback;
    var elements = collectProductGridElements(neoDocument.sections, []);
    grids.forEach(function (grid, index) {
      if (grid.getAttribute('data-aico-builder-settings') === '1') {
        return;
      }
      grid.setAttribute('data-aico-builder-settings', '1');
      var element = elements.length === grids.length ? elements[index] : null;
      var content = (element && element.content) || {};
      var bar = grid.previousElementSibling && grid.previousElementSibling.classList.contains('aico-product-grid-filters') ? grid.previousElementSibling : null;
      var targets = bar ? [grid, bar] : [grid];
      targets.forEach(function (target) {
        target.style.setProperty('--aico-button-background', content.buttonColor || accent);
        target.style.setProperty('--aico-button-color', content.buttonTextColor || '#ffffff');
        target.style.setProperty('--aico-filter-active', content.filterActiveColor || accent);
        target.style.setProperty('--aico-pill-background', settings.filterPillBg || pillBackgroundFallback);
        target.style.setProperty('--aico-pill-border', settings.filterPillBorder || pillBorderFallback);
      });
      if (bar && (content.filterAlignment === 'center' || content.filterAlignment === 'right')) {
        bar.style.justifyContent = content.filterAlignment === 'center' ? 'center' : 'flex-end';
      }
      if (!element) {
        return;
      }
      if (content.layoutVariant) {
        grid.setAttribute('data-layout', content.layoutVariant);
      }
      var isPlainCard = content.cardStyle === 'plain';
      if (isPlainCard) {
        grid.setAttribute('data-card-style', 'plain');
      }
      var products = Array.isArray(content.products) ? content.products : [];
      grid.querySelectorAll('.content-type-neo-product-card').forEach(function (card) {
        var product = findCardProduct(card, products);
        if (!product) {
          return;
        }
        var body = card.querySelector('.aico-news-card-body');
        if (body && product.brandName && !body.querySelector('.aico-news-card-brand')) {
          var brand = document.createElement('p');
          brand.className = 'aico-news-card-brand';
          brand.textContent = product.brandName;
          body.insertBefore(brand, body.firstChild);
        }
        var cardColor = String(product.cardBgColor || '').trim() || String(content.cardBackgroundColor || '').trim();
        if (cardColor && !isPlainCard) {
          card.style.setProperty('--aico-card-background', cardColor);
          card.style.setProperty('--aico-card-color', readableTextOn(cardColor));
        }
      });
    });
  }

  function finishGrids() {
    var grids = document.querySelectorAll('[data-aico-product-grid]:not([data-aico-finished])');
    if (grids.length === 0 || !shopDomain) {
      return;
    }
    injectStyles();
    grids.forEach(function (grid) {
      grid.setAttribute('data-aico-finished', '1');
      buildFilterBar(grid);
      grid.querySelectorAll('.content-type-neo-product-card').forEach(buildCardLayout);
      grid.querySelectorAll('[data-product-handles]').forEach(function (card) {
        var handlesByShop = parseJson(card.getAttribute('data-product-handles'), {});
        var handle = handlesByShop[shopDomain];
        if (!handle) {
          removeCardLink(card);
          return;
        }
        linkCardTo(card, handle, handlesByShop);
        addShopDetails(grid, card, handle);
      });
    });
    applyBuilderSettings();
  }

  function start() {
    // The news scripts load the article with jQuery; its answer carries the builder document.
    if (window.jQuery) {
      window.jQuery(document).ajaxComplete(function (event, request, options) {
        if (options && /\/aico\/theme\/news\?/.test(options.url || '')) {
          rememberNeoDocument(request.responseJSON || parseJson(request.responseText, null));
        }
      });
    }
    finishGrids();
    // The news scripts draw the article after their API call, so watch for the grids to appear.
    new MutationObserver(finishGrids).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
